import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Zap, Brain, Cloud, Palette, ArrowRight, ChevronDown, ExternalLink } from 'lucide-react';

const services = [
  {
    id: 'software',
    num: '01',
    icon: <Zap size={32} />,
    color: '#00d4aa',
    title: 'Software Engineering',
    tagline: 'Precision Code. Scalable Systems.',
    description:
      'We architect and build software that handles real-world complexity. From custom enterprise systems to consumer-grade mobile apps and robust APIs  we engineer for performance, scale, and longevity.',
    subServices: [
      {
        title: 'Custom Software Development',
        desc: 'Bespoke applications designed around your exact workflows, processes, and user needs.',
      },
      {
        title: 'Web Applications',
        desc: 'High-performance, responsive web apps built with modern frameworks like React, Next.js, and Vue.',
      },
      {
        title: 'Enterprise Systems',
        desc: 'Complex, mission-critical systems with high availability, role-based access, and integration layers.',
      },
      {
        title: 'Mobile Applications',
        desc: 'Cross-platform and native iOS/Android apps with polished UX and backend integration.',
      },
      {
        title: 'API Development',
        desc: 'RESTful and GraphQL APIs with proper auth, rate limiting, documentation, and versioning.',
      },
    ],
    outcomes: ['50% faster delivery', 'Zero technical debt architecture', '99.9% uptime SLAs'],
    img: 'https://images.unsplash.com/photo-1556075798-4825dfaaf498?w=700&q=80',
  },
  {
    id: 'ai',
    num: '02',
    icon: <Brain size={32} />,
    color: '#06b6d4',
    title: 'AI & Machine Learning',
    tagline: 'Intelligence Built Into Your Core.',
    description:
      'We embed intelligent systems into your products and processes  from conversational AI to predictive analytics, computer vision, and full-stack AI SaaS platforms. We work with leading LLMs and build custom ML pipelines.',
    subServices: [
      {
        title: 'AI Integration',
        desc: 'Connect your existing systems to LLMs like GPT-4, Claude, and Gemini for smart automation.',
      },
      {
        title: 'AI Chatbots',
        desc: 'Conversational agents for customer support, lead gen, and internal knowledge bases.',
      },
      {
        title: 'Data Analytics',
        desc: 'End-to-end data pipelines, dashboards, and predictive models that surface actionable insights.',
      },
      {
        title: 'Natural Language Processing',
        desc: 'Text classification, sentiment analysis, summarization, and document intelligence systems.',
      },
      {
        title: 'Computer Vision',
        desc: 'Image classification, object detection, OCR, and visual quality control systems.',
      },
      {
        title: 'Blockchain Integration',
        desc: 'Smart contracts, NFT platforms, and decentralized app backends on Ethereum and Solana.',
      },
      {
        title: 'AI SaaS Platforms',
        desc: 'End-to-end AI-powered products  from model training to scalable multi-tenant SaaS.',
      },
      {
        title: 'AI-Powered Business Systems',
        desc: 'Intelligent CRMs, ERPs, and workflow tools that learn and improve over time.',
      },
    ],
    outcomes: ['10x operational efficiency', 'Automated decision pipelines', 'Real-time intelligence'],
    img: 'https://images.unsplash.com/photo-1677442135703-1787eea5ce01?w=700&q=80',
  },
  {
    id: 'cloud',
    num: '03',
    icon: <Cloud size={32} />,
    color: '#6366f1',
    title: 'Cloud & DevOps',
    tagline: 'Infrastructure That Never Sleeps.',
    description:
      'We design, deploy, and maintain bulletproof cloud infrastructure on AWS, GCP, and Azure. From CI/CD pipelines to Kubernetes clusters, security hardening and 24/7 uptime monitoring  your infrastructure is our responsibility.',
    subServices: [
      {
        title: 'Infrastructure Setup',
        desc: 'Cloud-native architecture design and implementation on AWS, GCP, Azure, or hybrid stacks.',
      },
      {
        title: 'Security & Optimization',
        desc: 'Security audits, IAM policies, encrypted pipelines, and cost optimization strategies.',
      },
      {
        title: 'Maintenance & Support',
        desc: '24/7 monitoring, incident response, performance tuning, and ongoing SRE support.',
      },
    ],
    outcomes: ['99.99% uptime guaranteed', '60% cloud cost reduction', 'SOC2 compliance ready'],
    img: 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=700&q=80',
  },
  {
    id: 'design',
    num: '04',
    icon: <Palette size={32} />,
    color: '#8b5cf6',
    title: 'UI/UX Design',
    tagline: 'Experiences Users Remember.',
    description:
      'We design digital products that users love  combining research, strategy, and craft. From design systems to interactive prototypes and final production assets, we bridge the gap between beautiful and functional.',
    subServices: [
      {
        title: 'Product Design',
        desc: 'End-to-end product design from concept through wireframes, visual design, and handoff.',
      },
      {
        title: 'UX Strategy',
        desc: 'User research, journey mapping, and UX audits that eliminate friction and boost conversions.',
      },
      {
        title: 'Design Systems',
        desc: 'Scalable component libraries and design tokens that keep your brand consistent at any scale.',
      },
      {
        title: 'Interactive Prototyping',
        desc: 'High-fidelity, clickable prototypes that let you validate ideas before writing a single line of code.',
      },
    ],
    outcomes: ['3x conversion improvement', 'Consistent brand identity', 'Faster design-to-dev handoff'],
    img: 'https://images.unsplash.com/photo-1642286941365-89da3e29c0a2?w=700&q=80',
  },
];

const Services = () => {
  const [activeService, setActiveService] = useState(null);

  return (
    <main data-testid="services-page">
      {/* Hero */}
      <section
        style={{
          minHeight: '50vh',
          background: '#0a0f1e',
          display: 'flex',
          alignItems: 'flex-end',
          paddingTop: 'clamp(96px, 14vw, 120px)',
          paddingBottom: 'clamp(56px, 8vw, 80px)',
          borderBottom: '1px solid rgba(255,255,255,0.05)',
        }}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 w-full">
          <motion.span
            className="section-label"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
          >
            // Our Services
          </motion.span>
          <motion.h1
            style={{
              fontFamily: 'Manrope, sans-serif',
              fontWeight: 800,
              fontSize: 'clamp(2.5rem, 6vw, 5rem)',
              letterSpacing: '-0.04em',
              color: '#fff',
              lineHeight: 1.0,
              maxWidth: '700px',
            }}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.15 }}
          >
            What We
            <span className="text-gradient-teal"> Build.</span>
          </motion.h1>
          <motion.p
            style={{
              color: '#64748b',
              fontFamily: 'Inter',
              fontSize: '1.1rem',
              lineHeight: 1.75,
              maxWidth: '520px',
              marginTop: '20px',
            }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.3 }}
          >
            Four practice areas. One integrated team. Everything you need to build, launch, and scale.
          </motion.p>
        </div>
      </section>

      {/* Services list */}
      <section style={{ background: '#0a0f1e', padding: '0 0 clamp(80px, 10vw, 120px)' }}>
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {services.map((service, i) => (
            <motion.div
              key={service.id}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1 }}
              viewport={{ once: true, margin: '-80px' }}
              style={{
                borderBottom: '1px solid rgba(255,255,255,0.06)',
                padding: 'clamp(56px, 8vw, 80px) 0',
              }}
              data-testid={`service-section-${service.id}`}
            >
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
                {/* Text column */}
                <div className={i % 2 === 1 ? 'lg:order-2' : ''}>
                  <div className="flex items-center gap-4 mb-6">
                    <div
                      style={{
                        width: '56px',
                        height: '56px',
                        borderRadius: '14px',
                        background: `rgba(${service.color === '#00d4aa' ? '0,212,170' : service.color === '#06b6d4' ? '6,182,212' : service.color === '#6366f1' ? '99,102,241' : '139,92,246'}, 0.1)`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: service.color,
                      }}
                    >
                      {service.icon}
                    </div>
                    <span
                      style={{
                        fontFamily: 'JetBrains Mono',
                        fontSize: '0.72rem',
                        color: 'rgba(255,255,255,0.3)',
                        letterSpacing: '0.15em',
                      }}
                    >
                      {service.num} / 04
                    </span>
                  </div>

                  <h2
                    style={{
                      fontFamily: 'Manrope, sans-serif',
                      fontWeight: 800,
                      fontSize: 'clamp(1.8rem, 3.5vw, 2.8rem)',
                      letterSpacing: '-0.03em',
                      color: '#fff',
                      lineHeight: 1.1,
                      marginBottom: '8px',
                    }}
                  >
                    {service.title}
                  </h2>
                  <p
                    style={{
                      fontFamily: 'Manrope, sans-serif',
                      fontSize: '1rem',
                      color: service.color,
                      fontWeight: 600,
                      marginBottom: '20px',
                      letterSpacing: '-0.01em',
                    }}
                  >
                    {service.tagline}
                  </p>
                  <p
                    style={{
                      color: '#94a3b8',
                      fontFamily: 'Inter',
                      fontSize: '0.95rem',
                      lineHeight: 1.8,
                      marginBottom: '32px',
                    }}
                  >
                    {service.description}
                  </p>

                  {/* Outcomes */}
                  <div className="flex flex-col gap-3 mb-8">
                    {service.outcomes.map((o) => (
                      <div key={o} className="flex items-center gap-3">
                        <div
                          style={{
                            width: '20px',
                            height: '20px',
                            borderRadius: '50%',
                            background: `rgba(${service.color === '#00d4aa' ? '0,212,170' : service.color === '#06b6d4' ? '6,182,212' : service.color === '#6366f1' ? '99,102,241' : '139,92,246'}, 0.12)`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                            color: service.color,
                          }}
                        >
                          <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                            <path d="M1 4L3.5 6.5L9 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </div>
                        <span style={{ color: '#94a3b8', fontFamily: 'Inter', fontSize: '0.9rem' }}>{o}</span>
                      </div>
                    ))}
                  </div>

                  <button
                    onClick={() => setActiveService(activeService === service.id ? null : service.id)}
                    className="btn-secondary"
                    style={{ fontSize: '0.85rem', gap: '8px' }}
                    data-testid={`service-expand-${service.id}`}
                  >
                    {activeService === service.id ? 'Hide Details' : 'View Capabilities'}
                    <ChevronDown
                      size={16}
                      style={{
                        transform: activeService === service.id ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.3s ease',
                      }}
                    />
                  </button>
                </div>

                {/* Image column */}
                <div className={`${i % 2 === 1 ? 'lg:order-1' : ''}`}>
                  <div
                    className="glass-card"
                    style={{
                      overflow: 'hidden',
                      height: '360px',
                      position: 'relative',
                    }}
                  >
                    <img
                      src={service.img}
                      alt={service.title}
                      loading="lazy"
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        opacity: 0.7,
                      }}
                    />
                    <div
                      style={{
                        position: 'absolute',
                        inset: 0,
                        background: `linear-gradient(135deg, ${service.color}18 0%, transparent 70%)`,
                      }}
                    />
                    <div
                      style={{
                        position: 'absolute',
                        bottom: '24px',
                        left: '24px',
                        right: '24px',
                      }}
                    >
                      <span
                        style={{
                          fontFamily: 'Manrope',
                          fontWeight: 700,
                          fontSize: '1.1rem',
                          color: '#fff',
                          textShadow: '0 2px 12px rgba(0,0,0,0.8)',
                        }}
                      >
                        {service.subServices.length} capabilities available
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Expandable sub-services */}
              <AnimatePresence>
                {activeService === service.id && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.45 }}
                    style={{ overflow: 'hidden' }}
                  >
                    <div
                      className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
                      style={{ marginTop: '40px', paddingTop: '40px', borderTop: '1px solid rgba(255,255,255,0.06)' }}
                    >
                      {service.subServices.map((sub, j) => (
                        <motion.div
                          key={sub.title}
                          className="glass-card"
                          style={{ padding: '24px' }}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: j * 0.06 }}
                        >
                          <h4
                            style={{
                              fontFamily: 'Manrope',
                              fontWeight: 700,
                              fontSize: '1rem',
                              color: '#fff',
                              marginBottom: '8px',
                            }}
                          >
                            {sub.title}
                          </h4>
                          <p style={{ color: '#64748b', fontFamily: 'Inter', fontSize: '0.85rem', lineHeight: 1.65 }}>
                            {sub.desc}
                          </p>
                        </motion.div>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section
        style={{
          padding: 'clamp(72px, 9vw, 100px) 0',
          background: '#0d1b35',
          textAlign: 'center',
        }}
      >
        <div className="max-w-3xl mx-auto px-6">
          <span className="section-label" style={{ display: 'block', textAlign: 'center', marginBottom: '20px' }}>// Let’s Work Together</span>
          <h2
            style={{
              fontFamily: 'Manrope',
              fontWeight: 800,
              fontSize: 'clamp(1.8rem, 4vw, 3rem)',
              color: '#fff',
              letterSpacing: '-0.03em',
              lineHeight: 1.1,
              marginBottom: '32px',
            }}
          >
            Have a project in mind?{' '}
            <span className="text-gradient-teal">Let’s talk.</span>
          </h2>
          <Link to="/contact" className="btn-primary" style={{ fontSize: '1rem', padding: '15px 36px' }} data-testid="services-contact-cta">
            Start a Conversation <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </main>
  );
};

export default Services;
